// scrollRestoration is a tab-wide flag, not scoped to this page, so only force
// 'manual' when we actually have our own state to restore (checked as early as
// possible, before the browser's native restoration can run and clobber ours)
// - then hand control back to the browser once our restore is done, so every
// other page in the tab keeps native scroll restoration working normally.
if ('scrollRestoration' in history && history.state && history.state.__infiniteScroll) {
  history.scrollRestoration = 'manual';
}

class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.productsContainerEl = this.querySelector('#product-grid');
    this.paginationEl = this.querySelector('.pagination');
    this.loadingIndicator = this.querySelector('.infinite-scroll-spinner');
    this.nextPageUrl = null;
    this.loadedPageUrls = [];

    if (!this.productsContainerEl || !this.paginationEl) return;

    // Extract next page URL immediately from the rendered pagination
    this.nextPageUrl = this.getNextPageUrl(this.paginationEl);

    // pagehide is the fallback; the click listener is the primary trigger since
    // iOS Safari can suspend/kill a background tab's JS before pagehide fires.
    window.addEventListener('pagehide', this.saveState.bind(this));
    this.productsContainerEl.addEventListener('click', (event) => {
      if (event.target.closest('a[href]')) this.saveState();
    });
    // WebKit (Safari AND Chrome on iOS, same engine) bfcaches this page on back
    // navigation regardless of Cache-Control: no-store - the constructor above does
    // NOT re-run on a bfcache restore, so without this listener the settle loop never
    // fires and scrollRestoration stays stuck on 'manual' with nothing to act on it,
    // landing at the top. Pages are already in the DOM from before freeze, so this
    // only needs to re-apply scroll, never re-fetch.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      const saved = history.state && history.state.__infiniteScroll;
      if (saved) this.settleScroll(saved.scrollY);
    });

    if (this.tryRestoreState()) return; // restoreState finishes setup itself

    if (!this.nextPageUrl) return; // Only one page, nothing to do

    this.paginationEl.style.visibility = 'hidden';
    this.startObserving();
  }

  startObserving() {
    try {
      this.observer = this.registerObserver(this.loadNextPage.bind(this));
      this.observer.observe(this.paginationEl);
    } catch (error) {
      console.error('InfiniteScroll error:', error);
    }
  }

  // Shopify sends Cache-Control: no-store, but WebKit's bfcache ignores that header,
  // so back navigation can be either a fresh reload (this path, re-fetches the
  // extra pages) or a bfcache restore (handled by the pageshow listener above,
  // which skips re-fetching since the DOM is still there). Either way the saved
  // scroll position needs to be re-applied, since native restore is unreliable
  // once pages have been appended by infinite scroll.
  //
  // State lives on history.state, not sessionStorage: Safari (especially Private
  // Browsing) doesn't reliably persist sessionStorage across a real page reload, but
  // history.state is part of the session-history entry itself, not the Storage API,
  // so it isn't subject to that restriction and is inherently scoped to exactly the
  // entry the user is navigating back to.
  tryRestoreState() {
    const saved = history.state && history.state.__infiniteScroll;
    if (!saved || !Array.isArray(saved.pageUrls)) return false;

    const MAX_AGE_MS = 30 * 60 * 1000;
    if (typeof saved.savedAt !== 'number' || Date.now() - saved.savedAt > MAX_AGE_MS) return false;

    // Even with zero extra pages (user clicked a product while still on page 1),
    // we still want to restore plain scroll position - native reload-scroll memory
    // is unreliable on its own, which is exactly the gap this plugs.
    this.paginationEl.style.visibility = 'hidden';
    this.restoreState(saved);
    return true;
  }

  async restoreState(saved) {
    if (this.loadingIndicator) this.loadingIndicator.style.display = 'flex';

    for (const pageUrl of saved.pageUrls) {
      try {
        await this.fetchAndAppend(pageUrl);
      } catch (error) {
        console.error('Infinite scroll restore fetch error:', error);
        break;
      }
    }

    if (this.loadingIndicator) this.loadingIndicator.style.display = 'none';

    this.settleScroll(saved.scrollY);

    if (this.nextPageUrl) this.startObserving();
  }

  // iOS Safari lays out appended images late and can apply its own scroll
  // restore after ours, so keep re-applying the target until it sticks.
  settleScroll(target) {
    let attempts = 0;
    const settle = () => {
      attempts++;
      window.scrollTo(0, target);
      const reached = Math.abs(window.scrollY - target) < 4;
      if (attempts < 60 && !(reached && attempts > 3)) {
        requestAnimationFrame(settle);
      } else if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }
    };
    requestAnimationFrame(settle);
  }

  saveState() {
    // Always save scroll position, even with zero extra pages loaded - clicking a
    // product while still on page 1 is common (products_per_page is often 16-24,
    // easy to scroll deep into page 1 on a phone without crossing the page-2
    // trigger), and native reload-scroll memory for that case is unreliable on
    // its own in Safari.
    if (window.scrollY === 0) return;
    // Merge onto the current history entry's state rather than replacing it, so we
    // don't clobber state other scripts may have put there (e.g. cart drawer state).
    const state = Object.assign({}, history.state, {
      __infiniteScroll: { pageUrls: this.loadedPageUrls, scrollY: window.scrollY, savedAt: Date.now() },
    });
    history.replaceState(state, '');
  }

  getNextPageUrl(paginationEl) {
    // Dawn's pagination uses aria-label="Next page" on the next link
    const ariaNext = paginationEl.querySelector('a[aria-label="Next page"]');
    if (ariaNext) return ariaNext.getAttribute('href');

    // Fallback: look for pagination__item--next class
    const classNext = paginationEl.querySelector('.pagination__item--next a');
    if (classNext) return classNext.getAttribute('href');

    // Fallback: any link containing ?page= that isn't the previous page
    const pageLinks = paginationEl.querySelectorAll('a[href*="page="]');
    const currentPage = this.getCurrentPage();
    for (const link of pageLinks) {
      const url = new URL(link.href);
      const page = parseInt(url.searchParams.get('page'));
      if (page === currentPage + 1) return link.getAttribute('href');
    }

    return null;
  }

  getCurrentPage() {
    const url = new URL(window.location.href);
    return parseInt(url.searchParams.get('page') || '1');
  }

  registerObserver(callback) {
    let running = false;
    return new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !running) {
        running = true;
        callback()
          .then(() => running = false)
          .catch(() => running = false);
      }
    }, { rootMargin: '300px' });
  }

  loadNextPage() {
    if (!this.nextPageUrl) {
      this.observer.disconnect();
      if (this.loadingIndicator) this.loadingIndicator.style.display = 'none';
      return Promise.resolve();
    }

    if (this.loadingIndicator) this.loadingIndicator.style.display = 'flex';

    return this.fetchAndAppend(this.nextPageUrl)
      .then(() => {
        if (this.loadingIndicator) this.loadingIndicator.style.display = 'none';
        if (!this.nextPageUrl) {
          this.observer.disconnect();
          this.paginationEl.innerHTML = '';
        }
      })
      .catch(err => {
        if (this.loadingIndicator) this.loadingIndicator.style.display = 'none';
        console.error('Infinite scroll fetch error:', err);
      });
  }

  // Fetches one page, appends its products, and advances this.nextPageUrl /
  // this.loadedPageUrls. Shared by both live scrolling and state restoration.
  fetchAndAppend(urlToFetch) {
    return fetch(urlToFetch)
      .then(res => {
        if (!res.ok) throw new Error(res.statusText);
        return res.text();
      })
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Append new product items
        const newGrid = doc.querySelector('#product-grid');
        if (newGrid) {
          newGrid.querySelectorAll('.grid__item').forEach(item => {
            this.productsContainerEl.appendChild(item);
          });
        }

        this.loadedPageUrls.push(urlToFetch);

        // Get next page URL from the fetched page's pagination
        const newPagination = doc.querySelector('.pagination');
        this.nextPageUrl = newPagination ? this.getNextPageUrl(newPagination) : null;
      });
  }
}

customElements.define('infinite-scroll', InfiniteScroll);
